/**
 * «Подбор» — главная без хэша, `/podbor`, **без входа**: сотрудник салона держит её в закладках и отсюда
 * ищет платье по номеру серии или по фото с телефона. Ответ — та же страница `/podbor/<хэш>`: модель,
 * её склейка (все цвета), похожие по фото с наличием по салонам. Ниже — недавние поиски этого телефона.
 * Вид — как у страницы ответа (минимализм под Farfetch). Спека `docs/specs/2026-10-28-podbor-photo-design.md`.
 */
import { type FormEvent, useEffect, useState } from "react";
import { Link } from "react-router";

import { Shell, usePreview, useStartSearch } from "@/pages/PodborAnswerPage";
import { recentPodbor } from "@/lib/podborAnswer";

const ADDRESS = "prokat-rf.uni-renter.ru/podbor";

const CSS = `
.pod-hero{padding:28px var(--gx) 8px}
.pod-hero h1{font-family:var(--display);font-weight:400;font-size:30px;line-height:1.1;margin:8px 0 12px}
.pod-hero p{color:var(--ink-2);font-size:15px;max-width:520px}
.pod-way{padding:24px var(--gx) 0}
.pod-way h2{font-family:var(--display);font-weight:400;font-size:20px;margin-bottom:4px}
.pod-way .pod-mute{margin-bottom:12px}
.pod-way form{display:flex;gap:6px}
.pod-or{display:flex;align-items:center;gap:12px;padding:24px var(--gx) 0;color:var(--ink-2);font-size:12px;
  letter-spacing:.16em;text-transform:uppercase}
.pod-or::before,.pod-or::after{content:"";flex:1;border-top:1px solid var(--line)}
.pod-drop{display:flex;align-items:center;justify-content:center;gap:10px;width:100%;min-height:96px;
  border:1px dashed var(--ink-2)!important;font-size:15px;font-weight:500;cursor:pointer;text-align:center;padding:0 16px}
.pod-picked{display:grid;grid-template-columns:96px minmax(0,1fr);gap:14px;align-items:center}
.pod-picked img{width:96px;aspect-ratio:2/3;object-fit:cover;background:var(--tint)}
.pod-picked .acts{display:grid;gap:6px}
.pod-wide-btn{width:100%}
.pod-steps{margin:32px var(--gx) 0;padding:20px 0;border-top:1px solid var(--line);border-bottom:1px solid var(--line);
  display:grid;gap:12px;counter-reset:s}
.pod-steps li{list-style:none;display:grid;grid-template-columns:28px minmax(0,1fr);font-size:14px}
.pod-steps li::before{counter-increment:s;content:counter(s);font-family:var(--display);font-size:18px;line-height:1.2}
.pod-steps{padding-left:0}
.pod-save{padding:24px var(--gx) 0;display:grid;gap:6px}
.pod-save b{font-weight:500}
.pod-recent{padding:8px var(--gx) 0}
.pod-recent a{display:flex;align-items:center;gap:12px;min-height:56px;border-bottom:1px solid var(--line);text-decoration:none}
.pod-recent img,.pod-recent .ph{width:36px;height:48px;object-fit:cover;background:var(--tint);flex:0 0 auto}
.pod-recent .t{flex:1;font-size:14px}
.pod-recent .d{color:var(--ink-2);font-size:12px}
@media(min-width:900px){.pod-home-main{max-width:640px;margin:0 auto}}
`;

function when(iso: string): string {
  return new Date(iso).toLocaleString("ru-RU", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function PodborHomePage() {
  const [serial, setSerial] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const preview = usePreview(file);
  const { start, busy, error } = useStartSearch();
  const [copied, setCopied] = useState(false);
  const recent = recentPodbor();

  useEffect(() => {
    document.title = "Подбор платья · UNIRENTER";
  }, []);

  const bySerial = (e: FormEvent) => {
    e.preventDefault();
    void start(null, serial);
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`https://${ADDRESS}`);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Shell>
      <style>{CSS}</style>
      <main className="pod-main pod-home-main">
        <section className="pod-hero">
          <div className="pod-cap">Подбор платья</div>
          <h1>Найти платье по серии или по фото</h1>
          <p>
            Покажем модель, все её цвета (склейку) и похожие платья — с размерами и салонами, где они
            свободны прямо сейчас.
          </p>
        </section>

        <section className="pod-way">
          <h2>По номеру серии</h2>
          <p className="pod-mute">Номер — на бирке или в карточке товара CRM.</p>
          <form onSubmit={bySerial}>
            <input
              className="pod-input"
              inputMode="numeric"
              placeholder="Например, 9743050"
              aria-label="Номер серии"
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
            />
            <button type="submit" className="pod-btn dark" disabled={busy}>
              {busy && !file ? "Ищем…" : "Открыть"}
            </button>
          </form>
        </section>

        <div className="pod-or">или</div>

        <section className="pod-way">
          <h2>По фото</h2>
          <p className="pod-mute">
            Снимите платье или выберите фото из галереи — лучше платье целиком, без лишнего вокруг.
          </p>
          {preview ? (
            <div className="pod-picked">
              <img src={preview} alt="Выбранное фото" />
              <div className="acts">
                <button
                  type="button"
                  className="pod-btn dark pod-wide-btn"
                  disabled={busy}
                  onClick={() => void start(file, "")}
                >
                  {busy ? "Ищем похожие…" : "Найти похожие"}
                </button>
                <button type="button" className="pod-text" onClick={() => setFile(null)}>
                  Выбрать другое фото
                </button>
              </div>
            </div>
          ) : (
            <label className="pod-drop">
              Сфотографировать или выбрать фото
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  setFile(e.target.files?.[0] ?? null);
                  e.target.value = "";
                }}
              />
            </label>
          )}
        </section>

        {error ? (
          <div className="pod-err" style={{ paddingTop: 12 }} role="alert">
            {error}
          </div>
        ) : null}

        <ol className="pod-steps">
          <li>Откроется страница: эта модель, её цвета и похожие.</li>
          <li>Выберите город и размер, «Все фильтры» — цвет, силуэт, длина и другие.</li>
          <li>Размер со стрелкой ↗ открывает серию в CRM.</li>
        </ol>

        <section className="pod-save">
          <b>Сохраните страницу в закладки</b>
          <span className="pod-mute">
            Или «На экран „Домой“» в меню браузера — поиск будет под рукой. Адрес: {ADDRESS}
          </span>
          <button type="button" className="pod-link" onClick={() => void copy()} style={{ justifySelf: "start" }}>
            {copied ? "Ссылка скопирована" : "Скопировать ссылку"}
          </button>
        </section>

        {recent.length ? (
          <>
            <div className="pod-sec">
              <h2>Недавние поиски</h2>
              <span className="pod-mute">на этом телефоне</span>
            </div>
            <div className="pod-recent">
              {recent.map((r) => (
                <Link key={r.token} to={`/podbor/${r.token}`}>
                  {r.photo ? (
                    <img src={`/api/podbor/${encodeURIComponent(r.token)}/photo`} alt="" loading="lazy" />
                  ) : (
                    <span className="ph" />
                  )}
                  <span className="t">
                    {[r.serial ? `Серия ${r.serial}` : "", r.photo ? "по фото" : ""].filter(Boolean).join(" · ")}
                  </span>
                  <span className="d">{when(r.at)}</span>
                </Link>
              ))}
            </div>
          </>
        ) : null}

        <footer className="pod-foot">Наличие — на момент открытия. Бронь и готовность проверяйте в CRM.</footer>
      </main>
    </Shell>
  );
}
